import {
    IActivitySnapshot,
    IActivityHeatmapSnapshot,
    E_COMPONENT_TYPE,
} from '../services/lib/APIServiceInterfaces';
import { VIEW } from '../interfaces';
import {
    ActivityChartComponentStore,
    ActivityHeatmapComponentStore,
    ActivityMapComponentStore,
} from '../stores/insights/InsightsComponentStore';
import { generateID } from '../utils/generalUtils';

export const takeChartSnapshot = (
    rootStore,
    chartStore,
    currentActiveView: VIEW,
    props = {}
): IActivitySnapshot => {
    const { countriesArr, heatmapData } = props as any;
    const hasActiveCw = rootStore.customWeightStore.hasActiveCustomWeight;

    const snapshot = {
        virtualToday: chartStore.virtualToday.toDate(),
        baseDate: chartStore.baseDate.toDate(),
        countries: {
            isMultiSelect: currentActiveView !== VIEW.COUNTRY_GRAPH,
            values: rootStore.countryStore.currentStore.getValues(),
            colorsStack: rootStore.countryStore.serializeColors(),
        },
        risks: {
            isMultiSelect: currentActiveView === VIEW.COUNTRY_GRAPH,
            values: rootStore.risksStore.currentStore.getValues(),
            colorsStack: rootStore.risksStore.serializeColors(),
            currencyOn: rootStore.risksStore.currencyOn,
            projectionDataOn: rootStore.risksStore.projectionDataOn,
            riskChartMode: rootStore.risksStore.riskChartMode,
            activeGlobalIndicators: rootStore.risksStore.activeGlobalIndicators,
        },
        rangeLeft: chartStore.rangeLeft,
        rangeRight: chartStore.rangeRight,
        riskType: rootStore.risksStore.currentRisksType,
        showLines: chartStore.showLines,
        showCircles: chartStore.showCircles,
        showVirtualToday: chartStore.showVirtualToday,
        compositeDataInterval: chartStore.compositeDataInterval,
        activeGroup: countriesArr ? countriesArr.map(c => c.id) : [],
        hasActiveCustomWeight: hasActiveCw,
    };

    if (currentActiveView === VIEW.HEATMAP) {
        return {
            ...snapshot,
            ...{ heatmap: takeHeatmapSnapshot(rootStore, heatmapData) },
        };
    }

    return snapshot;
};

const takeHeatmapSnapshot = (
    rootStore,
    heatmapData
): IActivityHeatmapSnapshot => {
    const {
        selectedCountry,
        selectedRisk,
        startDate,
        timeSpan,
        riskType,
        scoreType,
        countriesList,
        selectedPercentile,
    } = heatmapData;

    return {
        countriesList,
        selectedCountry,
        selectedRisk,
        startDate,
        timeSpan,
        activeGroupId: rootStore.settingsStore.activeGroupId,
        riskType,
        scoreType,
        selectedPercentile,
    };
};

export const generateComponentFromSnapshot = (
    type: E_COMPONENT_TYPE,
    snapshot: IActivitySnapshot
):
    | ActivityMapComponentStore
    | ActivityChartComponentStore
    | ActivityHeatmapComponentStore => {
    const id = `temp-${generateID()}`;
    const htmlKey = `html-${generateID()}`;
    switch (type) {
        case E_COMPONENT_TYPE.CHART:
        case E_COMPONENT_TYPE.CONTINGENT_CHART:
            return new ActivityChartComponentStore({
                id,
                type,
                data: [],
                snapshot,
                name: '',
                htmlKey,
            });
        case E_COMPONENT_TYPE.DELTA:
        case E_COMPONENT_TYPE.RISK:
        case E_COMPONENT_TYPE.CR_DELTA:
        case E_COMPONENT_TYPE.CR_RISK:
        case E_COMPONENT_TYPE.MAP:
        case E_COMPONENT_TYPE.CONTINGENT_MAP:
            return new ActivityMapComponentStore({
                id,
                type,
                name: '',
                snapshot,
                htmlKey,
            });
        case E_COMPONENT_TYPE.HEATMAP:
            return new ActivityHeatmapComponentStore({
                id,
                type,
                name: '',
                snapshot,
                htmlKey,
            });
        default:
            return;
    }
};

export const getCurrentURLComponentType = (
    viewType: VIEW
): E_COMPONENT_TYPE => {
    switch (viewType) {
        case VIEW.COUNTRY_GRAPH:
        case VIEW.WORLD_GRAPH:
            return E_COMPONENT_TYPE.CHART;
        case VIEW.SORT_BY_DELTA:
            return E_COMPONENT_TYPE.DELTA;
        case VIEW.SORT_BY_RISK:
            return E_COMPONENT_TYPE.RISK;
        case VIEW.SORT_BY_CONTINGENT_RISK:
            return E_COMPONENT_TYPE.CR_RISK;
        case VIEW.SORT_BY_CONTINGENT_DELTA:
            return E_COMPONENT_TYPE.CR_DELTA;
        case VIEW.RISK_VIEW:
            return E_COMPONENT_TYPE.MAP;
        case VIEW.CONTINGENT:
            return E_COMPONENT_TYPE.CONTINGENT_MAP;
        case VIEW.HEATMAP:
            return E_COMPONENT_TYPE.HEATMAP;
        case VIEW.CONTINGENT_GRAPH:
            return E_COMPONENT_TYPE.CONTINGENT_CHART;
    }

    return null;
};
